import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { ArcusLoading } from '@/components/shared/ArcusLoading';
import { useSession } from '@/compositions/hooks/useSession';

import { useAppEntryStore } from '../stores/app-entry.store';

/**
 * Guarda de entrada: garante que a pessoa escolheu **como** entrar antes de ver o
 * app (RN-01).
 *
 * ## Ela não é uma guarda de autenticação
 *
 * Convidado passa. O que ela exige é uma escolha consciente entre entrar, criar
 * conta ou seguir como visitante — porque sem isso a tela inicial nunca
 * apareceria e os três caminhos da RN-01 seriam letra morta. Restrição de ação
 * por conta é outra coisa e mora no pop-up "Login necessário" (RN-23).
 *
 * ## Por que existe uma guarda, e não uma rota índice
 *
 * ⚠️ Aqui há uma tensão real entre dois documentos, e vale registrá-la:
 * `docs/regras-de-negocio/02` trata a tela inicial como a **primeira** tela do
 * app, enquanto `modules/map/routes.tsx` documenta a decisão de o mapa ser a rota
 * índice — porque é para lá que login, cadastro e entrada como convidado levam, e
 * um redirect de `/` para `/map` seria um salto visível em toda abertura.
 *
 * A guarda concilia os dois: `/` continua sendo o mapa e continua sendo o destino
 * de todos os fluxos, e a tela inicial aparece apenas enquanto não houve escolha.
 * Depois da primeira vez, ninguém mais passa por ela.
 *
 * O estado de carregamento é obrigatório: sem ele, o primeiro quadro renderiza
 * com `session: null` e manda para `/welcome` **todo** usuário já autenticado,
 * antes de a sessão ser lida.
 */
export function RequireAppEntry() {
  const { isAuthenticated, isLoading } = useSession();
  const isGuest = useAppEntryStore((state) => state.isGuest);
  const location = useLocation();

  if (isLoading) return <ArcusLoading className="h-dvh" />;

  if (!isAuthenticated && !isGuest) {
    // `state` preserva para onde a pessoa ia — um deep link de notificação não
    // deve ser perdido só porque ela ainda não tinha escolhido como entrar.
    return <Navigate to="/welcome" replace state={{ from: location.pathname }} />;
  }

  return <Outlet />;
}
